import React, { useState, useMemo, useRef, useEffect } from 'react'
import {
  FaArrowLeft,
  FaFileExcel,
  FaSearch,
  FaCalendarAlt,
  FaBuilding,
  FaTag,
  FaFolder,
  FaBoxes,
  FaCalendarWeek,
  FaChevronDown,
  FaTimes,
} from 'react-icons/fa'
import {
  XLSX,
  cellBannerTitle,
  cellBannerSubtitle,
  cellBannerMeta,
  cellColHeader,
  cellText,
  cellMoney,
  cellNumber,
  cellPercent,
  cellTotalLabel,
  cellTotalNumber,
  cellTotalPercent,
} from '../../utils/excelCorporateStyle'
import {
  AcompanhamentoDiarioResponse,
  AcompanhamentoDiarioParams,
} from './types'
import { SelectionModal, SelectionItem } from '../RelatorioScreen/SelectionModal'
import {
  DailyMatrixContainer,
  DailyMatrixTableScroll,
  DailyTable,
  MetricCellTag,
  SubHeaderBar,
  ResultTitle,
  FilterTagsRow,
  FilterTag,
  ActionButton,
  BackButton,
  SearchBar,
  EmptyState,
  StickyHorizontalScrollWrapper,
  FilterDropdownButton,
  WeekPillButton,
  ClearFiltersButton,
} from './styles'

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

interface Props {
  data: AcompanhamentoDiarioResponse
  params: AcompanhamentoDiarioParams
  onBack: () => void
}

interface SemanaDef {
  semanaNum: number
  label: string
  dias: number[]
}

export default function DailyTrackingMatrix({ data, params, onBack }: Props) {
  // ===========================================================================
  // 1. ESTADOS DE FILTROS ACUMULÁVEIS
  // ===========================================================================
  const [searchTerm, setSearchTerm] = useState('')

  // Distribuidores
  const [selectedDistIds, setSelectedDistIds] = useState<(string | number)[]>(
    params.modoAgrupamento === 'separado' && data.distribuidores.length > 0
      ? [data.distribuidores[0].id]
      : [],
  )
  const [showDistModal, setShowDistModal] = useState(false)

  // Marcas
  const [selectedMarcas, setSelectedMarcas] = useState<(string | number)[]>([])
  const [showMarcaModal, setShowMarcaModal] = useState(false)

  // Categorias
  const [selectedCategorias, setSelectedCategorias] = useState<(string | number)[]>([])
  const [showCategoriaModal, setShowCategoriaModal] = useState(false)

  // Famílias
  const [selectedFamilias, setSelectedFamilias] = useState<(string | number)[]>([])
  const [showFamiliaModal, setShowFamiliaModal] = useState(false)

  // Refs para scroll horizontal sincronizado
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const stickyScrollRef = useRef<HTMLDivElement>(null)
  const isSyncingScroll = useRef(false)
  const [contentScrollWidth, setContentScrollWidth] = useState(0)

  const totalDiasMes = data.ultimoDiaMes || 30
  const nomeMes = MONTH_NAMES[(data.mes - 1 + 12) % 12] || `Mês ${data.mes}`

  // Definição das semanas (S1 a S4 com 7 dias, S5 com os dias restantes)
  const semanas: SemanaDef[] = useMemo(() => {
    const list: SemanaDef[] = [
      { semanaNum: 1, label: 'Semana 1', dias: [1, 2, 3, 4, 5, 6, 7] },
      { semanaNum: 2, label: 'Semana 2', dias: [8, 9, 10, 11, 12, 13, 14] },
      { semanaNum: 3, label: 'Semana 3', dias: [15, 16, 17, 18, 19, 20, 21] },
      { semanaNum: 4, label: 'Semana 4', dias: [22, 23, 24, 25, 26, 27, 28] },
    ]

    const diasS5: number[] = []
    for (let d = 29; d <= totalDiasMes; d++) {
      diasS5.push(d)
    }

    if (diasS5.length > 0) {
      list.push({ semanaNum: 5, label: 'Semana 5', dias: diasS5 })
    }

    return list
  }, [totalDiasMes])

  // Filtro de semanas selecionadas (padrão: todas selecionadas)
  const [selectedSemanas, setSelectedSemanas] = useState<number[]>([1, 2, 3, 4, 5])

  const handleToggleSemana = (num: number) => {
    setSelectedSemanas((prev) => {
      if (prev.includes(num)) {
        if (prev.length === 1) return prev // manter pelo menos 1 semana ativa
        return prev.filter((s) => s !== num)
      }
      return [...prev, num].sort((a, b) => a - b)
    })
  }

  const handleSelectAllSemanas = () => {
    setSelectedSemanas(semanas.map((s) => s.semanaNum))
  }

  const semanasFiltradas = useMemo(() => {
    return semanas.filter((s) => selectedSemanas.includes(s.semanaNum))
  }, [semanas, selectedSemanas])

  const diasSemanasSelecionadas = useMemo(() => {
    return semanasFiltradas.flatMap((s) => s.dias)
  }, [semanasFiltradas])

  const labelSemanasAtivas = useMemo(() => {
    if (semanasFiltradas.length === semanas.length) return 'Todas as Semanas'
    return semanasFiltradas.map((s) => `S${s.semanaNum}`).join(', ')
  }, [semanasFiltradas, semanas.length])

  // Cálculo de dias decorridos para projeção de tendência
  const diasDecorridosNoMes = useMemo(() => {
    const hoje = new Date()
    const anoAtual = hoje.getFullYear()
    const mesAtual = hoje.getMonth() + 1
    const diaAtual = hoje.getDate()

    if (data.ano < anoAtual || (data.ano === anoAtual && data.mes < mesAtual)) {
      return totalDiasMes // Mês já concluído
    }
    if (data.ano > anoAtual || (data.ano === anoAtual && data.mes > mesAtual)) {
      return 0 // Mês futuro
    }
    return Math.min(diaAtual, totalDiasMes)
  }, [data.ano, data.mes, totalDiasMes])

  // ===========================================================================
  // 2. LISTAS E ITENS PARA MODAIS DE SELEÇÃO
  // ===========================================================================
  const distItems: SelectionItem[] = useMemo(() => {
    return data.distribuidores.map((d) => ({
      id: d.id,
      label: d.nome,
      extra: d.cod_dist ? `Cód: ${d.cod_dist}` : undefined,
    }))
  }, [data.distribuidores])

  const todasMarcas = useMemo(() => {
    if (data.marcas && data.marcas.length > 0) return data.marcas
    const setM = new Set<string>()
    data.vendasDiarias.forEach((v) => { if (v.marca) setM.add(v.marca) })
    data.vendasAnoAnterior.forEach((v) => { if (v.marca) setM.add(v.marca) })
    return Array.from(setM).sort()
  }, [data])

  const marcaItems: SelectionItem[] = useMemo(() => {
    return todasMarcas.map((m) => ({ id: m, label: m }))
  }, [todasMarcas])

  const todasCategorias = useMemo(() => {
    if (data.categorias && data.categorias.length > 0) return data.categorias
    const setC = new Set<string>()
    data.vendasDiarias.forEach((v) => { if (v.categoria) setC.add(v.categoria) })
    data.vendasAnoAnterior.forEach((v) => { if (v.categoria) setC.add(v.categoria) })
    return Array.from(setC).sort()
  }, [data])

  const categoriaItems: SelectionItem[] = useMemo(() => {
    return todasCategorias.map((c) => ({ id: c, label: c }))
  }, [todasCategorias])

  const familiaItems: SelectionItem[] = useMemo(() => {
    return data.familias.map((f) => ({ id: f, label: f }))
  }, [data.familias])

  const isTodosDist = useMemo(() => {
    return (
      selectedDistIds.length === 0 ||
      selectedDistIds.includes('__NONE__') ||
      selectedDistIds.length === data.distribuidores.length
    )
  }, [selectedDistIds, data.distribuidores.length])

  const labelDistribuidores = useMemo(() => {
    if (isTodosDist) return 'Todos os Distribuidores'
    if (selectedDistIds.length === 1) {
      const d = data.distribuidores.find((dist) => String(dist.id) === String(selectedDistIds[0]))
      return d ? d.nome : `Distribuidor #${selectedDistIds[0]}`
    }
    return `${selectedDistIds.length} Distribuidores`
  }, [isTodosDist, selectedDistIds, data.distribuidores])

  // Verificação de filtros ativos para botão "Limpar Filtros"
  const hasActiveFilters = useMemo(() => {
    return (
      !isTodosDist ||
      (selectedMarcas.length > 0 && !selectedMarcas.includes('__NONE__')) ||
      (selectedCategorias.length > 0 && !selectedCategorias.includes('__NONE__')) ||
      (selectedFamilias.length > 0 && !selectedFamilias.includes('__NONE__')) ||
      selectedSemanas.length < semanas.length ||
      searchTerm.trim().length > 0
    )
  }, [isTodosDist, selectedMarcas, selectedCategorias, selectedFamilias, selectedSemanas.length, semanas.length, searchTerm])

  const handleResetAllFilters = () => {
    setSelectedDistIds([])
    setSelectedMarcas([])
    setSelectedCategorias([])
    setSelectedFamilias([])
    setSelectedSemanas(semanas.map((s) => s.semanaNum))
    setSearchTerm('')
  }

  // ===========================================================================
  // 3. FILTRAGEM ACUMULADA DOS DADOS
  // ===========================================================================
  const { vendasDiariasFiltradas, vendasAnoAntFiltradas } = useMemo(() => {
    const vd = data.vendasDiarias.filter((v) => {
      if (!isTodosDist && !selectedDistIds.some((id) => String(id) === String(v.dist_id))) return false
      if (selectedMarcas.length > 0 && !selectedMarcas.includes('__NONE__') && !selectedMarcas.some((m) => String(m) === String(v.marca))) return false
      if (selectedCategorias.length > 0 && !selectedCategorias.includes('__NONE__') && !selectedCategorias.some((c) => String(c) === String(v.categoria))) return false
      if (selectedFamilias.length > 0 && !selectedFamilias.includes('__NONE__') && !selectedFamilias.some((f) => String(f) === String(v.familia))) return false
      return true
    })

    const va = data.vendasAnoAnterior.filter((v) => {
      if (!isTodosDist && !selectedDistIds.some((id) => String(id) === String(v.dist_id))) return false
      if (selectedMarcas.length > 0 && !selectedMarcas.includes('__NONE__') && !selectedMarcas.some((m) => String(m) === String(v.marca))) return false
      if (selectedCategorias.length > 0 && !selectedCategorias.includes('__NONE__') && !selectedCategorias.some((c) => String(c) === String(v.categoria))) return false
      if (selectedFamilias.length > 0 && !selectedFamilias.includes('__NONE__') && !selectedFamilias.some((f) => String(f) === String(v.familia))) return false
      return true
    })

    return { vendasDiariasFiltradas: vd, vendasAnoAntFiltradas: va }
  }, [data, isTodosDist, selectedDistIds, selectedMarcas, selectedCategorias, selectedFamilias])

  // Mapas de agregação diária e ano anterior por família
  const { mapaDiario, mapaAnoAnt, totalValorPorDia, totalCxPorDia, anoAntTotalValor, anoAntTotalCx } = useMemo(() => {
    const diario: Record<string, Record<number, { caixas: number; valor: number }>> = {}
    const anoAnt: Record<string, { caixas: number; valor: number }> = {}
    const tValorDia: Record<number, number> = {}
    const tCxDia: Record<number, number> = {}

    let tAnoAntVal = 0
    let tAnoAntCx = 0

    vendasDiariasFiltradas.forEach((v) => {
      const fam = v.familia
      const dia = v.dia
      if (!diario[fam]) diario[fam] = {}
      if (!diario[fam][dia]) diario[fam][dia] = { caixas: 0, valor: 0 }
      diario[fam][dia].caixas += v.total_caixas
      diario[fam][dia].valor += v.total_valor

      tValorDia[dia] = (tValorDia[dia] || 0) + v.total_valor
      tCxDia[dia] = (tCxDia[dia] || 0) + v.total_caixas
    })

    vendasAnoAntFiltradas.forEach((v) => {
      const fam = v.familia
      if (!anoAnt[fam]) anoAnt[fam] = { caixas: 0, valor: 0 }
      anoAnt[fam].caixas += v.total_caixas
      anoAnt[fam].valor += v.total_valor

      tAnoAntVal += v.total_valor
      tAnoAntCx += v.total_caixas
    })

    return {
      mapaDiario: diario,
      mapaAnoAnt: anoAnt,
      totalValorPorDia: tValorDia,
      totalCxPorDia: tCxDia,
      anoAntTotalValor: tAnoAntVal,
      anoAntTotalCx: tAnoAntCx,
    }
  }, [vendasDiariasFiltradas, vendasAnoAntFiltradas])

  // Lista de famílias ordenadas que possuem ao menos 1 dia com movimentação/venda
  const familiasFiltradas = useMemo(() => {
    const listComVenda = data.familias.filter((fam) => {
      if (!fam || !fam.trim()) return false
      const diasDaFam = mapaDiario[fam]
      if (!diasDaFam) return false
      return Object.values(diasDaFam).some(
        (x) => Math.abs(x.caixas) > 0.0001 || Math.abs(x.valor) > 0.0001,
      )
    })

    if (!searchTerm.trim()) return listComVenda
    const term = searchTerm.toLowerCase()
    return listComVenda.filter((f) => f.toLowerCase().includes(term))
  }, [data.familias, mapaDiario, searchTerm])

  // Atualizar largura do scroll horizontal para a barra sticky
  useEffect(() => {
    const updateWidth = () => {
      if (tableScrollRef.current) {
        setContentScrollWidth(tableScrollRef.current.scrollWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    const timer = setTimeout(updateWidth, 200)
    return () => {
      window.removeEventListener('resize', updateWidth)
      clearTimeout(timer)
    }
  }, [data, familiasFiltradas, semanasFiltradas])

  const handleTableScroll = () => {
    if (isSyncingScroll.current) return
    isSyncingScroll.current = true
    if (stickyScrollRef.current && tableScrollRef.current) {
      stickyScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft
    }
    requestAnimationFrame(() => {
      isSyncingScroll.current = false
    })
  }

  const handleStickyScroll = () => {
    if (isSyncingScroll.current) return
    isSyncingScroll.current = true
    if (tableScrollRef.current && stickyScrollRef.current) {
      tableScrollRef.current.scrollLeft = stickyScrollRef.current.scrollLeft
    }
    requestAnimationFrame(() => {
      isSyncingScroll.current = false
    })
  }

  // ===========================================================================
  // 4. HELPERS DE FORMATAÇÃO E CÁLCULO
  // ===========================================================================
  const fmtCx = (val: number) => {
    if (val === 0) return '-'
    return Math.round(val).toLocaleString('pt-BR')
  }

  const fmtPct = (val: number) => {
    if (isNaN(val) || !isFinite(val)) return '-'
    return `${val.toFixed(1)}%`
  }

  const getStatusColor = (pct: number): 'green' | 'yellow' | 'red' | 'neutral' => {
    if (isNaN(pct) || !isFinite(pct) || pct <= 0) return 'neutral'
    if (pct >= 100) return 'green'
    if (pct >= 85) return 'yellow'
    return 'red'
  }

  // Helper para cálculo de métricas de semana
  const calcMetricasSemana = (diasSemana: number[], getValDia: (dia: number) => number, metaMes: number) => {
    const realSemana = diasSemana.reduce((acc, d) => acc + (getValDia(d) || 0), 0)
    const metaSemana = metaMes * (diasSemana.length / totalDiasMes)
    const pctSemana = metaSemana > 0 ? (realSemana / metaSemana) * 100 : 0

    let tendenciaSemana = 0
    const diasDecorridosSemana = diasSemana.filter((d) => d <= diasDecorridosNoMes).length

    if (diasDecorridosSemana >= diasSemana.length) {
      tendenciaSemana = pctSemana
    } else if (diasDecorridosSemana > 0) {
      const realAteAgora = diasSemana
        .filter((d) => d <= diasDecorridosNoMes)
        .reduce((acc, d) => acc + (getValDia(d) || 0), 0)
      const projecao = (realAteAgora / diasDecorridosSemana) * diasSemana.length
      tendenciaSemana = metaSemana > 0 ? (projecao / metaSemana) * 100 : 0
    }

    return { realSemana, metaSemana, pctSemana, tendenciaSemana }
  }

  // Helper para cálculo do resumo do mês (31 dias cheios)
  const calcResumoMes = (realMes: number, anoAnt: number) => {
    const metaMes = anoAnt * 1.05 // Regra de Negócio: Ano Anterior + 5%
    const gap = realMes - metaMes
    const pctMeta = metaMes > 0 ? (realMes / metaMes) * 100 : 0
    const pctAnoAnt = anoAnt > 0 ? ((realMes - anoAnt) / anoAnt) * 100 : 0

    let tendenciaMes = 0
    if (diasDecorridosNoMes >= totalDiasMes) {
      tendenciaMes = pctMeta
    } else if (diasDecorridosNoMes > 0) {
      const projecaoMes = (realMes / diasDecorridosNoMes) * totalDiasMes
      tendenciaMes = metaMes > 0 ? (projecaoMes / metaMes) * 100 : 0
    }

    return { metaMes, gap, pctMeta, pctAnoAnt, tendenciaMes }
  }

  // Helper para cálculo da NOVA Seção 2: Resumo Consolidado das Semanas Selecionadas
  const calcResumoSemanas = (
    diasSemanas: number[],
    getValDia: (dia: number) => number,
    anoAntMes: number,
    metaMes: number,
  ) => {
    const fator = diasSemanas.length / totalDiasMes
    const anoAntSemanas = anoAntMes * fator
    const metaSemanas = metaMes * fator
    const realSemanas = diasSemanas.reduce((acc, d) => acc + (getValDia(d) || 0), 0)
    const gap = realSemanas - metaSemanas
    const pctMeta = metaSemanas > 0 ? (realSemanas / metaSemanas) * 100 : 0
    const pctAnoAnt = anoAntSemanas > 0 ? ((realSemanas - anoAntSemanas) / anoAntSemanas) * 100 : 0

    let tendenciaSemanas = 0
    const diasDecorridosSemanas = diasSemanas.filter((d) => d <= diasDecorridosNoMes).length

    if (diasDecorridosSemanas >= diasSemanas.length) {
      tendenciaSemanas = pctMeta
    } else if (diasDecorridosSemanas > 0) {
      const realAteAgora = diasSemanas
        .filter((d) => d <= diasDecorridosNoMes)
        .reduce((acc, d) => acc + (getValDia(d) || 0), 0)
      const projecao = (realAteAgora / diasDecorridosSemanas) * diasSemanas.length
      tendenciaSemanas = metaSemanas > 0 ? (projecao / metaSemanas) * 100 : 0
    }

    return { anoAntSemanas, metaSemanas, realSemanas, gap, pctMeta, pctAnoAnt, tendenciaSemanas }
  }

  // Totais do Mês e das Semanas Selecionadas
  const realMesValorTotal = useMemo(() => {
    return Object.values(totalValorPorDia).reduce((acc, v) => acc + v, 0)
  }, [totalValorPorDia])

  const realMesCxTotal = useMemo(() => {
    return Object.values(totalCxPorDia).reduce((acc, v) => acc + v, 0)
  }, [totalCxPorDia])

  const resumoMesValor = useMemo(() => {
    return calcResumoMes(realMesValorTotal, anoAntTotalValor)
  }, [realMesValorTotal, anoAntTotalValor])

  const resumoMesCx = useMemo(() => {
    return calcResumoMes(realMesCxTotal, anoAntTotalCx)
  }, [realMesCxTotal, anoAntTotalCx])

  // Totais das Semanas Selecionadas (Valor e Caixas)
  const resumoSemanasValor = useMemo(() => {
    return calcResumoSemanas(
      diasSemanasSelecionadas,
      (d) => totalValorPorDia[d] || 0,
      anoAntTotalValor,
      resumoMesValor.metaMes,
    )
  }, [diasSemanasSelecionadas, totalValorPorDia, anoAntTotalValor, resumoMesValor.metaMes])

  const resumoSemanasCx = useMemo(() => {
    return calcResumoSemanas(
      diasSemanasSelecionadas,
      (d) => totalCxPorDia[d] || 0,
      anoAntTotalCx,
      resumoMesCx.metaMes,
    )
  }, [diasSemanasSelecionadas, totalCxPorDia, anoAntTotalCx, resumoMesCx.metaMes])

  // Estilo visual de borda divisória entre seções
  const borderDividerStyle: React.CSSProperties = {
    borderRight: '2px solid rgba(166, 193, 11, 0.45)',
  }

  // ===========================================================================
  // 5. EXPORTAÇÃO PARA EXCEL (.xlsx) COMPLETA COM FILTROS E RESUMOS CORPORATIVOS
  // ===========================================================================
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new()
    const sheetData: any[][] = []

    // 1. Definição da quantidade total de colunas
    // Col 0: Linha / Família
    // Col 1 a 7: Resumo Consolidado do Mês (7 colunas)
    // Col 8 a 14: Resumo Semanas (7 colunas)
    // Col 15 em diante: Semanas Filtradas
    const totalSemanasCols = semanasFiltradas.reduce((acc, sem) => acc + sem.dias.length + 4, 0)
    const totalCols = 1 + 7 + 7 + totalSemanasCols

    // Rótulos descritivos dos filtros ativos para o Banner
    const labelMarcasFiltro =
      selectedMarcas.length > 0 && !selectedMarcas.includes('__NONE__')
        ? selectedMarcas.length === 1
          ? String(selectedMarcas[0])
          : `${selectedMarcas.length} Marcas`
        : 'Todas as Marcas'

    const labelCategoriasFiltro =
      selectedCategorias.length > 0 && !selectedCategorias.includes('__NONE__')
        ? selectedCategorias.length === 1
          ? String(selectedCategorias[0])
          : `${selectedCategorias.length} Categorias`
        : 'Todas as Categorias'

    const labelFamiliasFiltro =
      selectedFamilias.length > 0 && !selectedFamilias.includes('__NONE__')
        ? selectedFamilias.length === 1
          ? String(selectedFamilias[0])
          : `${selectedFamilias.length} Famílias`
        : `${familiasFiltradas.length} Famílias com Venda`

    const buscaFiltro = searchTerm.trim() ? ` | Busca: "${searchTerm.trim()}"` : ''

    const metaInfoStr = `Período: ${nomeMes}/${data.ano}   |   Distribuidor: ${labelDistribuidores}   |   Marcas: ${labelMarcasFiltro}   |   Categorias: ${labelCategoriasFiltro}   |   Famílias: ${labelFamiliasFiltro}${buscaFiltro}   |   Semanas: ${labelSemanasAtivas}   |   Emissão: ${new Date().toLocaleString('pt-BR')}`

    // 2. Banner Institucional Corporativo GID (Linhas 0, 1 e 2)
    const bannerTitle = 'GSA ALIMENTOS — SISTEMA DE GESTÃO INDIRETA (GID)'
    const bannerSubtitle = `ACOMPANHAMENTO DIÁRIO DE SELLOUT — ${nomeMes.toUpperCase()} / ${data.ano}`

    const row0: any[] = []
    const row1: any[] = []
    const row2: any[] = []
    const row3: any[] = []

    for (let c = 0; c < totalCols; c++) {
      row0.push(cellBannerTitle(c === 0 ? bannerTitle : ''))
      row1.push(cellBannerSubtitle(c === 0 ? bannerSubtitle : ''))
      row2.push(cellBannerMeta(c === 0 ? metaInfoStr : ''))
      row3.push({ v: '', t: 's' })
    }

    sheetData.push(row0)
    sheetData.push(row1)
    sheetData.push(row2)
    sheetData.push(row3)

    // 3. Cabeçalho Nível 1 (Grupos com Merges)
    const headerRow1: any[] = []
    // Col 0: Linha / Família (merge vertical com Linha 5)
    headerRow1.push(cellColHeader('Linha / Família', 'left'))

    // Col 1 a 7: Resumo Consolidado do Mês (7 colunas)
    headerRow1.push(cellColHeader(`Resumo Consolidado do Mês (${nomeMes})`, 'center'))
    for (let i = 1; i < 7; i++) headerRow1.push(cellColHeader('', 'center'))

    // Col 8 a 14: Resumo Semanas (7 colunas)
    headerRow1.push(cellColHeader(`Resumo Semanas (${labelSemanasAtivas})`, 'center'))
    for (let i = 1; i < 7; i++) headerRow1.push(cellColHeader('', 'center'))

    // Colunas de cada Semana Selecionada
    semanasFiltradas.forEach((sem) => {
      const colSpan = sem.dias.length + 4
      headerRow1.push(
        cellColHeader(
          `${sem.label} (${sem.dias[0]} a ${sem.dias[sem.dias.length - 1]} ${nomeMes})`,
          'center',
        ),
      )
      for (let i = 1; i < colSpan; i++) headerRow1.push(cellColHeader('', 'center'))
    })
    sheetData.push(headerRow1)

    // 4. Cabeçalho Nível 2 (Subcolunas)
    const headerRow2: any[] = []
    headerRow2.push(cellColHeader('', 'left')) // Col 0 (mesclada com row 4)

    // Subcolunas Resumo Mês
    headerRow2.push(cellColHeader('Ano Ant.', 'right'))
    headerRow2.push(cellColHeader('% AxA', 'right'))
    headerRow2.push(cellColHeader('Meta (+5%)', 'right'))
    headerRow2.push(cellColHeader('Real Mês', 'right'))
    headerRow2.push(cellColHeader('Gap Mês', 'right'))
    headerRow2.push(cellColHeader('% Ating.', 'right'))
    headerRow2.push(cellColHeader('Tendência', 'right'))

    // Subcolunas Resumo Semanas
    headerRow2.push(cellColHeader('Ano Ant.', 'right'))
    headerRow2.push(cellColHeader('% AxA', 'right'))
    headerRow2.push(cellColHeader('Meta Sem.', 'right'))
    headerRow2.push(cellColHeader('Real Sem.', 'right'))
    headerRow2.push(cellColHeader('Gap Sem.', 'right'))
    headerRow2.push(cellColHeader('% Ating.', 'right'))
    headerRow2.push(cellColHeader('Tendência', 'right'))

    // Subcolunas de cada Semana
    semanasFiltradas.forEach((sem) => {
      sem.dias.forEach((d) => {
        headerRow2.push(cellColHeader(`D${d}`, 'right'))
      })
      headerRow2.push(cellColHeader(`${sem.label} Meta`, 'right'))
      headerRow2.push(cellColHeader(`${sem.label} Real`, 'right'))
      headerRow2.push(cellColHeader(`${sem.label} %`, 'right'))
      headerRow2.push(cellColHeader(`${sem.label} Tend.`, 'right'))
    })
    sheetData.push(headerRow2)

    // 5. Linha 6: Valor Total (R$)
    const r1Row: any[] = [
      cellText('Valor Total (R$)', { bold: true, align: 'left' }),
      // Resumo Mês (Valor)
      cellMoney(anoAntTotalValor, false, true),
      cellPercent(resumoMesValor.pctAnoAnt, false, true),
      cellMoney(resumoMesValor.metaMes, false, true),
      cellMoney(realMesValorTotal, false, true),
      cellMoney(resumoMesValor.gap, false, true),
      cellPercent(resumoMesValor.pctMeta, false, true),
      cellPercent(resumoMesValor.tendenciaMes, false, true),
      // Resumo Semanas (Valor)
      cellMoney(resumoSemanasValor.anoAntSemanas, false, true),
      cellPercent(resumoSemanasValor.pctAnoAnt, false, true),
      cellMoney(resumoSemanasValor.metaSemanas, false, true),
      cellMoney(resumoSemanasValor.realSemanas, false, true),
      cellMoney(resumoSemanasValor.gap, false, true),
      cellPercent(resumoSemanasValor.pctMeta, false, true),
      cellPercent(resumoSemanasValor.tendenciaSemanas, false, true),
    ]

    semanasFiltradas.forEach((sem) => {
      const sMet = calcMetricasSemana(sem.dias, (d) => totalValorPorDia[d] || 0, resumoMesValor.metaMes)
      sem.dias.forEach((d) => r1Row.push(cellMoney(totalValorPorDia[d] || 0, false, true)))
      r1Row.push(
        cellMoney(sMet.metaSemana, false, true),
        cellMoney(sMet.realSemana, false, true),
        cellPercent(sMet.pctSemana, false, true),
        cellPercent(sMet.tendenciaSemana, false, true),
      )
    })
    sheetData.push(r1Row)

    // 6. Linhas das Famílias Filtradas
    familiasFiltradas.forEach((fam, famIdx) => {
      const isEven = famIdx % 2 === 1
      const fAnoAnt = mapaAnoAnt[fam]?.caixas || 0
      const fRealTotal = Object.values(mapaDiario[fam] || {}).reduce((acc, x) => acc + x.caixas, 0)
      const fResumoMes = calcResumoMes(fRealTotal, fAnoAnt)
      const fResumoSem = calcResumoSemanas(
        diasSemanasSelecionadas,
        (d) => mapaDiario[fam]?.[d]?.caixas || 0,
        fAnoAnt,
        fResumoMes.metaMes,
      )

      const fRow: any[] = [
        cellText(fam, isEven, 'left'),
        // Resumo Mês
        cellNumber(fAnoAnt, isEven),
        cellPercent(fResumoMes.pctAnoAnt, isEven),
        cellNumber(fResumoMes.metaMes, isEven),
        cellNumber(fRealTotal, isEven),
        cellNumber(fResumoMes.gap, isEven),
        cellPercent(fResumoMes.pctMeta, isEven),
        cellPercent(fResumoMes.tendenciaMes, isEven),
        // Resumo Semanas
        cellNumber(fResumoSem.anoAntSemanas, isEven),
        cellPercent(fResumoSem.pctAnoAnt, isEven),
        cellNumber(fResumoSem.metaSemanas, isEven),
        cellNumber(fResumoSem.realSemanas, isEven),
        cellNumber(fResumoSem.gap, isEven),
        cellPercent(fResumoSem.pctMeta, isEven),
        cellPercent(fResumoSem.tendenciaSemanas, isEven),
      ]

      semanasFiltradas.forEach((sem) => {
        const sMet = calcMetricasSemana(sem.dias, (d) => mapaDiario[fam]?.[d]?.caixas || 0, fResumoMes.metaMes)
        sem.dias.forEach((d) => fRow.push(cellNumber(mapaDiario[fam]?.[d]?.caixas || 0, isEven)))
        fRow.push(
          cellNumber(sMet.metaSemana, isEven),
          cellNumber(sMet.realSemana, isEven),
          cellPercent(sMet.pctSemana, isEven),
          cellPercent(sMet.tendenciaSemana, isEven),
        )
      })
      sheetData.push(fRow)
    })

    // 7. Linha de Total Geral (Caixas)
    const totalRow: any[] = [
      cellTotalLabel('Total Geral (Caixas)', 'left'),
      // Resumo Mês
      cellTotalNumber(anoAntTotalCx),
      cellTotalPercent(resumoMesCx.pctAnoAnt),
      cellTotalNumber(resumoMesCx.metaMes),
      cellTotalNumber(realMesCxTotal),
      cellTotalNumber(resumoMesCx.gap),
      cellTotalPercent(resumoMesCx.pctMeta),
      cellTotalPercent(resumoMesCx.tendenciaMes),
      // Resumo Semanas
      cellTotalNumber(resumoSemanasCx.anoAntSemanas),
      cellTotalPercent(resumoSemanasCx.pctAnoAnt),
      cellTotalNumber(resumoSemanasCx.metaSemanas),
      cellTotalNumber(resumoSemanasCx.realSemanas),
      cellTotalNumber(resumoSemanasCx.gap),
      cellTotalPercent(resumoSemanasCx.pctMeta),
      cellTotalPercent(resumoSemanasCx.tendenciaSemanas),
    ]

    semanasFiltradas.forEach((sem) => {
      const sMet = calcMetricasSemana(sem.dias, (d) => totalCxPorDia[d] || 0, resumoMesCx.metaMes)
      sem.dias.forEach((d) => totalRow.push(cellTotalNumber(totalCxPorDia[d] || 0)))
      totalRow.push(
        cellTotalNumber(sMet.metaSemana),
        cellTotalNumber(sMet.realSemana),
        cellTotalPercent(sMet.pctSemana),
        cellTotalPercent(sMet.tendenciaSemana),
      )
    })
    sheetData.push(totalRow)

    // 8. Construção da Folha com Merges, Larguras e Alturas
    const ws = XLSX.utils.aoa_to_sheet(sheetData)

    // Configuração dos Merges
    const lastColIdx = totalCols - 1
    const merges: any[] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastColIdx } }, // Banner 1
      { s: { r: 1, c: 0 }, e: { r: 1, c: lastColIdx } }, // Banner 2
      { s: { r: 2, c: 0 }, e: { r: 2, c: lastColIdx } }, // Banner 3
      { s: { r: 4, c: 0 }, e: { r: 5, c: 0 } },           // Linha / Família (merge vertical)
      { s: { r: 4, c: 1 }, e: { r: 4, c: 7 } },           // Resumo Consolidado do Mês
      { s: { r: 4, c: 8 }, e: { r: 4, c: 14 } },          // Resumo Semanas
    ]

    let currColIdx = 15
    semanasFiltradas.forEach((sem) => {
      const span = sem.dias.length + 4
      merges.push({
        s: { r: 4, c: currColIdx },
        e: { r: 4, c: currColIdx + span - 1 },
      })
      currColIdx += span
    })

    ws['!merges'] = merges

    // Configuração das Larguras das Colunas
    const colWidths: { wch: number }[] = []
    colWidths.push({ wch: 28 }) // Linha / Família
    // Resumo Mês (7 colunas)
    colWidths.push({ wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 })
    // Resumo Semanas (7 colunas)
    colWidths.push({ wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 })
    // Semanas detalhadas
    semanasFiltradas.forEach((sem) => {
      sem.dias.forEach(() => {
        colWidths.push({ wch: 8 })
      })
      colWidths.push({ wch: 12 }) // Meta
      colWidths.push({ wch: 12 }) // Real
      colWidths.push({ wch: 10 }) // %
      colWidths.push({ wch: 10 }) // Tendência
    })
    ws['!cols'] = colWidths

    // Configuração de Alturas de Linhas
    const rowHeights: { hpt: number }[] = [
      { hpt: 26 }, // Banner 1
      { hpt: 22 }, // Banner 2
      { hpt: 18 }, // Metadados
      { hpt: 10 }, // Espaçador
      { hpt: 24 }, // Header Linha 1 (Grupos)
      { hpt: 22 }, // Header Linha 2 (Subcolunas)
      { hpt: 20 }, // Linha 1 (Valor em R$)
      ...familiasFiltradas.map(() => ({ hpt: 19 })),
      { hpt: 23 }, // Total Geral
    ]
    ws['!rows'] = rowHeights

    XLSX.utils.book_append_sheet(wb, ws, 'Acompanhamento Diário')
    XLSX.writeFile(
      wb,
      `Acompanhamento_Diario_${nomeMes}_${data.ano}_${labelDistribuidores.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`,
    )
  }

  // ===========================================================================
  // 6. RENDER DA MATRIZ DIÁRIA
  // ===========================================================================
  return (
    <DailyMatrixContainer>
      {/* HEADER DE COMANDOS */}
      <SubHeaderBar style={{ alignItems: 'flex-start' }}>
        <div>
          <ResultTitle style={{ textAlign: 'left', display: 'block', margin: '0 0 6px 0' }}>
            Acompanhamento Diário de Sellout
          </ResultTitle>
          <FilterTagsRow>
            <FilterTag>
              <FaCalendarAlt size={11} style={{ marginRight: 4 }} />
              <strong>Período:</strong> {nomeMes}/{data.ano}
            </FilterTag>
            <FilterTag>
              <FaBuilding size={11} style={{ marginRight: 4 }} />
              <strong>Distribuidores:</strong> {labelDistribuidores}
            </FilterTag>
            {diasDecorridosNoMes < totalDiasMes && diasDecorridosNoMes > 0 && (
              <FilterTag style={{ background: '#fef3c7', color: '#92400e', borderColor: '#f59e0b' }}>
                <strong>Dias decorridos:</strong> {diasDecorridosNoMes} de {totalDiasMes} dias
              </FilterTag>
            )}
          </FilterTagsRow>
        </div>

        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <SearchBar style={{ width: 220 }}>
            <FaSearch size={13} />
            <input
              type="text"
              placeholder="Buscar família..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>

          <ActionButton onClick={handleExportExcel} title="Exportar Matriz em Planilha Excel">
            <FaFileExcel size={15} color="#22c55e" />
            <span>Excel (.xlsx)</span>
          </ActionButton>

          <BackButton
            onClick={onBack}
            title="Voltar para a seleção de filtros"
            style={{ minWidth: 125, padding: '10px 22px', fontSize: '0.86rem' }}
          >
            <FaArrowLeft size={13} />
            <span>Voltar</span>
          </BackButton>
        </div>
      </SubHeaderBar>

      {/* BARRA DE FILTROS AVANÇADOS ACUMULÁVEIS (DISTRIBUIDOR, MARCA, CATEGORIA, FAMÍLIA, SEMANAS) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
          background: 'rgba(0, 24, 66, 0.45)',
          padding: '10px 16px',
          borderRadius: 14,
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        {/* BOTÃO 1: DISTRIBUIDORES (MODAL) */}
        <FilterDropdownButton
          type="button"
          $active={!isTodosDist}
          onClick={() => setShowDistModal(true)}
          title="Selecionar distribuidores participantes"
        >
          <FaBuilding size={13} />
          <span>{labelDistribuidores}</span>
          <span className="badge">
            {isTodosDist ? data.distribuidores.length : selectedDistIds.length}
          </span>
          <FaChevronDown size={10} style={{ opacity: 0.7 }} />
        </FilterDropdownButton>

        {/* BOTÃO 2: MARCAS (MODAL) */}
        <FilterDropdownButton
          type="button"
          $active={selectedMarcas.length > 0 && !selectedMarcas.includes('__NONE__')}
          onClick={() => setShowMarcaModal(true)}
          title="Filtrar marcas"
        >
          <FaTag size={13} />
          <span>
            {selectedMarcas.length === 0 || selectedMarcas.includes('__NONE__')
              ? 'Todas as Marcas'
              : selectedMarcas.length === 1
              ? String(selectedMarcas[0])
              : `${selectedMarcas.length} Marcas`}
          </span>
          <span className="badge">
            {selectedMarcas.length === 0 || selectedMarcas.includes('__NONE__')
              ? todasMarcas.length
              : selectedMarcas.length}
          </span>
          <FaChevronDown size={10} style={{ opacity: 0.7 }} />
        </FilterDropdownButton>

        {/* BOTÃO 3: CATEGORIAS (MODAL) */}
        <FilterDropdownButton
          type="button"
          $active={selectedCategorias.length > 0 && !selectedCategorias.includes('__NONE__')}
          onClick={() => setShowCategoriaModal(true)}
          title="Filtrar categorias"
        >
          <FaFolder size={13} />
          <span>
            {selectedCategorias.length === 0 || selectedCategorias.includes('__NONE__')
              ? 'Todas as Categorias'
              : selectedCategorias.length === 1
              ? String(selectedCategorias[0])
              : `${selectedCategorias.length} Categorias`}
          </span>
          <span className="badge">
            {selectedCategorias.length === 0 || selectedCategorias.includes('__NONE__')
              ? todasCategorias.length
              : selectedCategorias.length}
          </span>
          <FaChevronDown size={10} style={{ opacity: 0.7 }} />
        </FilterDropdownButton>

        {/* BOTÃO 4: FAMÍLIAS (MODAL) */}
        <FilterDropdownButton
          type="button"
          $active={selectedFamilias.length > 0 && !selectedFamilias.includes('__NONE__')}
          onClick={() => setShowFamiliaModal(true)}
          title="Filtrar famílias de produtos"
        >
          <FaBoxes size={13} />
          <span>
            {selectedFamilias.length === 0 || selectedFamilias.includes('__NONE__')
              ? 'Todas as Famílias'
              : selectedFamilias.length === 1
              ? String(selectedFamilias[0])
              : `${selectedFamilias.length} Famílias`}
          </span>
          <span className="badge">
            {selectedFamilias.length === 0 || selectedFamilias.includes('__NONE__')
              ? data.familias.length
              : selectedFamilias.length}
          </span>
          <FaChevronDown size={10} style={{ opacity: 0.7 }} />
        </FilterDropdownButton>

        {/* DIVISÓRIA SUTIL */}
        <div style={{ width: 1, height: 24, background: 'rgba(255, 255, 255, 0.15)' }} />

        {/* SELETOR ACUMULÁVEL DE SEMANAS */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.74rem', fontWeight: 700, opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
            <FaCalendarWeek size={12} /> Semanas:
          </span>
          {semanas.map((sem) => {
            const isSelected = selectedSemanas.includes(sem.semanaNum)
            return (
              <WeekPillButton
                key={sem.semanaNum}
                type="button"
                $active={isSelected}
                onClick={() => handleToggleSemana(sem.semanaNum)}
                title={`Alternar Semana ${sem.semanaNum}`}
              >
                S{sem.semanaNum}
              </WeekPillButton>
            )
          })}
          <WeekPillButton
            type="button"
            $active={selectedSemanas.length === semanas.length}
            onClick={handleSelectAllSemanas}
            title="Selecionar todas as semanas"
          >
            Todas
          </WeekPillButton>
        </div>

        {/* BOTÃO LIMPAR FILTROS */}
        {hasActiveFilters && (
          <ClearFiltersButton type="button" onClick={handleResetAllFilters} title="Limpar todos os filtros ativos">
            <FaTimes size={11} /> Limpar Filtros
          </ClearFiltersButton>
        )}
      </div>

      {/* TABELA MATRIZ */}
      <DailyMatrixTableScroll ref={tableScrollRef} onScroll={handleTableScroll}>
        <DailyTable>
          <thead>
            {/* LINHA 1 DE CABEÇALHO */}
            <tr>
              <th rowSpan={2} className="sticky-col" style={{ verticalAlign: 'middle' }}>
                Linha / Família
              </th>

              {/* SEÇÃO 1: RESUMO CONSOLIDADO DO MÊS (SEMPRE PRESENTE) */}
              <th colSpan={7} className="week-group-header month-summary" style={borderDividerStyle}>
                Resumo Consolidado do Mês ({nomeMes})
              </th>

              {/* SEÇÃO 2: RESUMO CONSOLIDADO DAS SEMANAS SELECIONADAS */}
              <th colSpan={7} className="week-group-header weeks-summary" style={borderDividerStyle}>
                Resumo Semanas ({labelSemanasAtivas})
              </th>

              {/* SEÇÃO 3: SEMANAS DETALHADAS SELECIONADAS */}
              {semanasFiltradas.map((sem) => (
                <th
                  key={sem.semanaNum}
                  colSpan={sem.dias.length + 4}
                  className={`week-group-header week-${sem.semanaNum}`}
                >
                  {sem.label} ({sem.dias[0]} a {sem.dias[sem.dias.length - 1]} {nomeMes})
                </th>
              ))}
            </tr>

            {/* LINHA 2 DE CABEÇALHO: SUBCOLUNAS */}
            <tr>
              {/* Subcolunas do Resumo do Mês */}
              <th style={{ minWidth: 85, textAlign: 'right' }}>Ano Ant.</th>
              <th style={{ minWidth: 65, textAlign: 'right' }}>% AxA</th>
              <th style={{ minWidth: 90, textAlign: 'right' }}>Meta (+5%)</th>
              <th style={{ minWidth: 90, textAlign: 'right' }}>Real Mês</th>
              <th style={{ minWidth: 80, textAlign: 'right' }}>Gap</th>
              <th style={{ minWidth: 65, textAlign: 'right' }}>% Meta</th>
              <th style={{ minWidth: 70, textAlign: 'right', ...borderDividerStyle }}>Tendência</th>

              {/* Subcolunas do Resumo das Semanas Selecionadas */}
              <th style={{ minWidth: 85, textAlign: 'right' }}>Ano Ant.</th>
              <th style={{ minWidth: 65, textAlign: 'right' }}>% AxA</th>
              <th style={{ minWidth: 90, textAlign: 'right' }}>Meta Sem.</th>
              <th style={{ minWidth: 90, textAlign: 'right' }}>Real Sem.</th>
              <th style={{ minWidth: 80, textAlign: 'right' }}>Gap</th>
              <th style={{ minWidth: 65, textAlign: 'right' }}>% Meta</th>
              <th style={{ minWidth: 70, textAlign: 'right', ...borderDividerStyle }}>Tendência</th>

              {/* Subcolunas das Semanas Selecionadas */}
              {semanasFiltradas.map((sem) => (
                <React.Fragment key={sem.semanaNum}>
                  {sem.dias.map((dia) => (
                    <th key={dia} style={{ minWidth: 42, textAlign: 'right', padding: '6px 4px' }}>
                      {dia}
                    </th>
                  ))}
                  <th style={{ minWidth: 75, textAlign: 'right', background: 'rgba(0,0,0,0.18)' }}>Meta</th>
                  <th style={{ minWidth: 75, textAlign: 'right', background: 'rgba(0,0,0,0.22)' }}>Real</th>
                  <th style={{ minWidth: 65, textAlign: 'right', background: 'rgba(0,0,0,0.18)' }}>%</th>
                  <th style={{ minWidth: 70, textAlign: 'right', background: 'rgba(0,0,0,0.22)' }}>Tend.</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* LINHA 1: VALOR EM R$ */}
            <tr className="row-valor-rs">
              <td className="sticky-col">Valor Total (R$)</td>

              {/* Seção 1: Resumo Mês (Valor) */}
              <td>R$ {fmtCx(anoAntTotalValor)}</td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoMesValor.pctAnoAnt)}>
                  {fmtPct(resumoMesValor.pctAnoAnt)}
                </MetricCellTag>
              </td>
              <td>R$ {fmtCx(resumoMesValor.metaMes)}</td>
              <td>R$ {fmtCx(realMesValorTotal)}</td>
              <td style={{ color: resumoMesValor.gap >= 0 ? '#4ade80' : '#f87171' }}>
                R$ {fmtCx(resumoMesValor.gap)}
              </td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoMesValor.pctMeta)}>
                  {fmtPct(resumoMesValor.pctMeta)}
                </MetricCellTag>
              </td>
              <td style={borderDividerStyle}>
                <MetricCellTag $status={getStatusColor(resumoMesValor.tendenciaMes)}>
                  {fmtPct(resumoMesValor.tendenciaMes)}
                </MetricCellTag>
              </td>

              {/* Seção 2: Resumo Semanas Selecionadas (Valor) */}
              <td>R$ {fmtCx(resumoSemanasValor.anoAntSemanas)}</td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoSemanasValor.pctAnoAnt)}>
                  {fmtPct(resumoSemanasValor.pctAnoAnt)}
                </MetricCellTag>
              </td>
              <td>R$ {fmtCx(resumoSemanasValor.metaSemanas)}</td>
              <td>R$ {fmtCx(resumoSemanasValor.realSemanas)}</td>
              <td style={{ color: resumoSemanasValor.gap >= 0 ? '#4ade80' : '#f87171' }}>
                R$ {fmtCx(resumoSemanasValor.gap)}
              </td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoSemanasValor.pctMeta)}>
                  {fmtPct(resumoSemanasValor.pctMeta)}
                </MetricCellTag>
              </td>
              <td style={borderDividerStyle}>
                <MetricCellTag $status={getStatusColor(resumoSemanasValor.tendenciaSemanas)}>
                  {fmtPct(resumoSemanasValor.tendenciaSemanas)}
                </MetricCellTag>
              </td>

              {/* Seção 3: Semanas Selecionadas (Valor) */}
              {semanasFiltradas.map((sem) => {
                const sMet = calcMetricasSemana(
                  sem.dias,
                  (d) => totalValorPorDia[d] || 0,
                  resumoMesValor.metaMes,
                )
                return (
                  <React.Fragment key={sem.semanaNum}>
                    {sem.dias.map((d) => (
                      <td key={d}>{fmtCx(totalValorPorDia[d] || 0)}</td>
                    ))}
                    <td>{fmtCx(sMet.metaSemana)}</td>
                    <td>{fmtCx(sMet.realSemana)}</td>
                    <td>
                      <MetricCellTag $status={getStatusColor(sMet.pctSemana)}>
                        {fmtPct(sMet.pctSemana)}
                      </MetricCellTag>
                    </td>
                    <td>
                      <MetricCellTag $status={getStatusColor(sMet.tendenciaSemana)}>
                        {fmtPct(sMet.tendenciaSemana)}
                      </MetricCellTag>
                    </td>
                  </React.Fragment>
                )
              })}
            </tr>

            {/* LINHAS DE FAMÍLIAS */}
            {familiasFiltradas.length === 0 ? (
              <tr>
                <td colSpan={15 + semanasFiltradas.reduce((acc, s) => acc + s.dias.length + 4, 0)}>
                  <EmptyState>Nenhuma família encontrada com os filtros selecionados.</EmptyState>
                </td>
              </tr>
            ) : (
              familiasFiltradas.map((fam) => {
                const fAnoAnt = mapaAnoAnt[fam]?.caixas || 0
                const fRealTotal = Object.values(mapaDiario[fam] || {}).reduce((acc, x) => acc + x.caixas, 0)
                const fResumoMes = calcResumoMes(fRealTotal, fAnoAnt)
                const fResumoSem = calcResumoSemanas(
                  diasSemanasSelecionadas,
                  (d) => mapaDiario[fam]?.[d]?.caixas || 0,
                  fAnoAnt,
                  fResumoMes.metaMes,
                )

                return (
                  <tr key={fam}>
                    <td className="sticky-col" title={fam}>
                      {fam}
                    </td>

                    {/* Seção 1: Resumo Mês (Caixas) */}
                    <td>{fmtCx(fAnoAnt)}</td>
                    <td>
                      <MetricCellTag $status={getStatusColor(fResumoMes.pctAnoAnt)}>
                        {fmtPct(fResumoMes.pctAnoAnt)}
                      </MetricCellTag>
                    </td>
                    <td>{fmtCx(fResumoMes.metaMes)}</td>
                    <td>{fmtCx(fRealTotal)}</td>
                    <td style={{ color: fResumoMes.gap >= 0 ? '#4ade80' : '#f87171' }}>
                      {fmtCx(fResumoMes.gap)}
                    </td>
                    <td>
                      <MetricCellTag $status={getStatusColor(fResumoMes.pctMeta)}>
                        {fmtPct(fResumoMes.pctMeta)}
                      </MetricCellTag>
                    </td>
                    <td style={borderDividerStyle}>
                      <MetricCellTag $status={getStatusColor(fResumoMes.tendenciaMes)}>
                        {fmtPct(fResumoMes.tendenciaMes)}
                      </MetricCellTag>
                    </td>

                    {/* Seção 2: Resumo Semanas Selecionadas (Caixas) */}
                    <td>{fmtCx(fResumoSem.anoAntSemanas)}</td>
                    <td>
                      <MetricCellTag $status={getStatusColor(fResumoSem.pctAnoAnt)}>
                        {fmtPct(fResumoSem.pctAnoAnt)}
                      </MetricCellTag>
                    </td>
                    <td>{fmtCx(fResumoSem.metaSemanas)}</td>
                    <td>{fmtCx(fResumoSem.realSemanas)}</td>
                    <td style={{ color: fResumoSem.gap >= 0 ? '#4ade80' : '#f87171' }}>
                      {fmtCx(fResumoSem.gap)}
                    </td>
                    <td>
                      <MetricCellTag $status={getStatusColor(fResumoSem.pctMeta)}>
                        {fmtPct(fResumoSem.pctMeta)}
                      </MetricCellTag>
                    </td>
                    <td style={borderDividerStyle}>
                      <MetricCellTag $status={getStatusColor(fResumoSem.tendenciaSemanas)}>
                        {fmtPct(fResumoSem.tendenciaSemanas)}
                      </MetricCellTag>
                    </td>

                    {/* Seção 3: Semanas Selecionadas (Caixas) */}
                    {semanasFiltradas.map((sem) => {
                      const sMet = calcMetricasSemana(
                        sem.dias,
                        (d) => mapaDiario[fam]?.[d]?.caixas || 0,
                        fResumoMes.metaMes,
                      )
                      return (
                        <React.Fragment key={sem.semanaNum}>
                          {sem.dias.map((d) => (
                            <td key={d}>{fmtCx(mapaDiario[fam]?.[d]?.caixas || 0)}</td>
                          ))}
                          <td>{fmtCx(sMet.metaSemana)}</td>
                          <td>{fmtCx(sMet.realSemana)}</td>
                          <td>
                            <MetricCellTag $status={getStatusColor(sMet.pctSemana)}>
                              {fmtPct(sMet.pctSemana)}
                            </MetricCellTag>
                          </td>
                          <td>
                            <MetricCellTag $status={getStatusColor(sMet.tendenciaSemana)}>
                              {fmtPct(sMet.tendenciaSemana)}
                            </MetricCellTag>
                          </td>
                        </React.Fragment>
                      )
                    })}
                  </tr>
                )
              })
            )}

            {/* LINHA FINAL: TOTAL GERAL EM CAIXAS */}
            <tr className="row-total-geral">
              <td className="sticky-col">Total Geral (Caixas)</td>

              {/* Seção 1: Resumo Mês (Total Caixas) */}
              <td>{fmtCx(anoAntTotalCx)}</td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoMesCx.pctAnoAnt)}>
                  {fmtPct(resumoMesCx.pctAnoAnt)}
                </MetricCellTag>
              </td>
              <td>{fmtCx(resumoMesCx.metaMes)}</td>
              <td>{fmtCx(realMesCxTotal)}</td>
              <td style={{ color: resumoMesCx.gap >= 0 ? '#4ade80' : '#f87171' }}>
                {fmtCx(resumoMesCx.gap)}
              </td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoMesCx.pctMeta)}>
                  {fmtPct(resumoMesCx.pctMeta)}
                </MetricCellTag>
              </td>
              <td style={borderDividerStyle}>
                <MetricCellTag $status={getStatusColor(resumoMesCx.tendenciaMes)}>
                  {fmtPct(resumoMesCx.tendenciaMes)}
                </MetricCellTag>
              </td>

              {/* Seção 2: Resumo Semanas Selecionadas (Total Caixas) */}
              <td>{fmtCx(resumoSemanasCx.anoAntSemanas)}</td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoSemanasCx.pctAnoAnt)}>
                  {fmtPct(resumoSemanasCx.pctAnoAnt)}
                </MetricCellTag>
              </td>
              <td>{fmtCx(resumoSemanasCx.metaSemanas)}</td>
              <td>{fmtCx(resumoSemanasCx.realSemanas)}</td>
              <td style={{ color: resumoSemanasCx.gap >= 0 ? '#4ade80' : '#f87171' }}>
                {fmtCx(resumoSemanasCx.gap)}
              </td>
              <td>
                <MetricCellTag $status={getStatusColor(resumoSemanasCx.pctMeta)}>
                  {fmtPct(resumoSemanasCx.pctMeta)}
                </MetricCellTag>
              </td>
              <td style={borderDividerStyle}>
                <MetricCellTag $status={getStatusColor(resumoSemanasCx.tendenciaSemanas)}>
                  {fmtPct(resumoSemanasCx.tendenciaSemanas)}
                </MetricCellTag>
              </td>

              {/* Seção 3: Semanas Selecionadas (Total Caixas) */}
              {semanasFiltradas.map((sem) => {
                const sMet = calcMetricasSemana(
                  sem.dias,
                  (d) => totalCxPorDia[d] || 0,
                  resumoMesCx.metaMes,
                )
                return (
                  <React.Fragment key={sem.semanaNum}>
                    {sem.dias.map((d) => (
                      <td key={d}>{fmtCx(totalCxPorDia[d] || 0)}</td>
                    ))}
                    <td>{fmtCx(sMet.metaSemana)}</td>
                    <td>{fmtCx(sMet.realSemana)}</td>
                    <td>
                      <MetricCellTag $status={getStatusColor(sMet.pctSemana)}>
                        {fmtPct(sMet.pctSemana)}
                      </MetricCellTag>
                    </td>
                    <td>
                      <MetricCellTag $status={getStatusColor(sMet.tendenciaSemana)}>
                        {fmtPct(sMet.tendenciaSemana)}
                      </MetricCellTag>
                    </td>
                  </React.Fragment>
                )
              })}
            </tr>
          </tbody>
        </DailyTable>
      </DailyMatrixTableScroll>

      {/* BARRA DE ROLAGEM HORIZONTAL FIXA / STICKY NO RODAPÉ DA VIEWPORT */}
      <StickyHorizontalScrollWrapper ref={stickyScrollRef} onScroll={handleStickyScroll}>
        <div style={{ width: contentScrollWidth || 2500, height: 1 }} />
      </StickyHorizontalScrollWrapper>

      {/* MODAL 1: DISTRIBUIDORES */}
      <SelectionModal
        isOpen={showDistModal}
        title="Selecionar Distribuidores"
        icon={<FaBuilding size={18} color="#a6c10b" />}
        items={distItems}
        selectedIds={selectedDistIds}
        onConfirm={(ids) => setSelectedDistIds(ids)}
        onClose={() => setShowDistModal(false)}
        allLabel="Todos os Distribuidores"
      />

      {/* MODAL 2: MARCAS */}
      <SelectionModal
        isOpen={showMarcaModal}
        title="Selecionar Marcas"
        icon={<FaTag size={18} color="#a6c10b" />}
        items={marcaItems}
        selectedIds={selectedMarcas}
        onConfirm={(ids) => setSelectedMarcas(ids)}
        onClose={() => setShowMarcaModal(false)}
        allLabel="Todas as Marcas"
      />

      {/* MODAL 3: CATEGORIAS */}
      <SelectionModal
        isOpen={showCategoriaModal}
        title="Selecionar Categorias"
        icon={<FaFolder size={18} color="#a6c10b" />}
        items={categoriaItems}
        selectedIds={selectedCategorias}
        onConfirm={(ids) => setSelectedCategorias(ids)}
        onClose={() => setShowCategoriaModal(false)}
        allLabel="Todas as Categorias"
      />

      {/* MODAL 4: FAMÍLIAS */}
      <SelectionModal
        isOpen={showFamiliaModal}
        title="Selecionar Famílias"
        icon={<FaBoxes size={18} color="#a6c10b" />}
        items={familiaItems}
        selectedIds={selectedFamilias}
        onConfirm={(ids) => setSelectedFamilias(ids)}
        onClose={() => setShowFamiliaModal(false)}
        allLabel="Todas as Famílias"
      />
    </DailyMatrixContainer>
  )
}
